/** Tutor form helpers: DTO builders, hydration, validation. */
import { getCompanyCountry } from '@/stores/themeStore';
import {
  formBool,
  hydrateUserContactFields,
  nextContactFields,
  phoneToE164,
  primaryContactKindFromType,
} from '@/utils/user-contact-utils';
import {
  addressFieldsFromSelection,
  fileNameFromUrl as fileNameFromSharedUrl,
  normalizeInPersonExitReason,
  setNestedPath as setSharedNestedPath,
} from '@/utils/common-utils';
import { parseDepartmentCodeFromText } from '@/utils/department-lookup-utils';
import { toCalendarDateString, toLocalDateString } from '@/utils/format-date-utils';
import type { ProfileDto as InPersonTutorProfileDto } from '@/types/inperson/tutor.types';
import type { Program } from '@/types/program.types';
import { applyCatalogCoursePath } from '@/utils/program-cascade-utils';
import { uploadFile } from '@/services/files.service';
import { getErrorMessage } from '@/utils/errors-utils';
import type { AddressSelectionResult } from '@/types/geography.types';
import { TUTOR_MIN_AGE, TUTOR_SUBJECT_UUID_REGEX } from '@/components/tutors/tutor-configs';
import { storedDocumentLink } from '@/utils/pending-document-link-utils';
import {
  flattenProfessionalSituation,
  serializeProfessionalSituation,
} from '@/utils/professional-situation-utils';

export {
  formatAssignmentRefusalReason,
  getAssignmentDecisionHeader,
  getTutorAssignmentStatusColor,
  getTutorAssignmentStatusLabel,
} from '@/utils/common-utils';

/** Mutation used to validate / reset a criminal-record decision. */
export type CriminalRecordDecisionFn = (args: {
  userId: string;
  companyId: string;
  action: 'validate' | 'cancel';
}) => Promise<unknown>;

/** Form fields that hold uploaded document URLs. */
type DocumentLinkFields = {
  contractDocumentLink?: string;
  certDocumentLink?: string;
  criminalRecordDocumentLink?: string;
};

/** Last path segment of a stored file URL. */
export function fileNameFromUrl(url: string): string {
  return fileNameFromSharedUrl(url);
}

/** Immutable set of a dotted path on a form object. */
export function setNestedPath<T extends Record<string, unknown>>(
  obj: T,
  path: string,
  value: unknown,
): T {
  return setSharedNestedPath(obj, path, value);
}

/** Next email/phone fields after a contact-type change. */
export function applyInPersonTutorContactChange(
  set: (field: string, value: unknown) => void,
  previousContactType: string | undefined,
  value: string,
  type: string,
  countryCode?: string,
): void {
  const next = nextContactFields({
    previousType: previousContactType,
    value,
    type,
    countryCode,
    defaultCountry: countryCode ?? '',
    previousSecondary: 'keep',
  });
  set('contact', next.contact);
  set('contactType', next.contactType);
  if (next.contactCountryCode) set('contactCountryCode', next.contactCountryCode);
  if (next.secondaryContact === '') set('secondaryContact', '');
}

/** Upload a file; returns the stored URL. */
export function defaultUpload(file: File): Promise<string> {
  return uploadFile(file);
}

/** True when at least one selected file will upload before the tutor save API. */
export function hasPendingUploadFiles(
  pendingFiles: Record<string, File | null | undefined>,
): boolean {
  return Object.values(pendingFiles).some(Boolean);
}

/** Upload every pending file in parallel; returns key → stored URL. */
export async function uploadPendingTutorFiles(
  pendingFiles: Record<string, File | null | undefined>,
  upload: (file: File) => Promise<string> = defaultUpload,
): Promise<Record<string, string>> {
  const queued = Object.entries(pendingFiles).filter((entry): entry is [string, File] =>
    Boolean(entry[1]),
  );
  const uploaded = await Promise.all(
    queued.map(([key, file]) => upload(file).then((url) => [key, url] as const)),
  );
  return Object.fromEntries(uploaded);
}

/** Parse a department code from free text. */
function extractDepartmentCode(value: string | undefined): string | undefined {
  return parseDepartmentCodeFromText(value);
}

/** BAN selection → address + department code. */
export function inPersonTutorAddressFromAutocomplete(
  result: AddressSelectionResult,
): Record<string, string> {
  const code = result.departmentCode || extractDepartmentCode(result.department);
  return {
    ...addressFieldsFromSelection(result),
    ...(code ? { department: code } : {}),
  };
}

/** Flatten onboarding sections into one form bag (later keys overwrite). */
function mergeSectionValues(sections: Record<string, unknown>): Record<string, any> {
  let merged: Record<string, any> = {};
  for (const sec of Object.values(sections)) {
    if (sec && typeof sec === 'object') {
      merged = { ...merged, ...(sec as Record<string, any>) };
    }
  }
  return merged;
}

/** Copy departments (normalize objects → ids) and wish establishment ids. */
function applyEstablishmentsToForm(
  merged: Record<string, any>,
  myEstablishments: Record<string, any> | undefined,
) {
  if (!myEstablishments) return;
  if (myEstablishments.departments) {
    // Normalize: backend may return objects {departmentId, departmentName} — extract string ID
    merged.departments = (myEstablishments.departments as any[]).map((d: any) =>
      typeof d === 'string' ? d : (d?.departmentId ?? d?.id ?? d),
    );
  }
  if (myEstablishments.wishes) {
    merged.selectedEstablishments = myEstablishments.wishes.map((w: any) => w.establishmentId);
  }
}

/** Copy information-meeting participation fields. */
function applyInfoSessionToForm(
  merged: Record<string, any>,
  infoSession: Record<string, any> | undefined,
) {
  if (infoSession) {
    merged.hasParticipated = formBool(infoSession.hasParticipated);
    merged.sessionId = infoSession.sessionId;
    merged.participatedAt = infoSession.participatedAt;
    return;
  }
  merged.hasParticipated = formBool(merged.hasParticipated);
}

/** Copy criminal-record + identity-document fields onto the form bag. */
function applyDocumentsToForm(
  merged: Record<string, any>,
  myDocuments: Record<string, any> | undefined,
) {
  if (!myDocuments) return;
  if (myDocuments.criminalRecord) {
    const criminalRecord = { ...myDocuments.criminalRecord };
    if (!criminalRecord.fileName && criminalRecord.documentName) {
      criminalRecord.fileName = criminalRecord.documentName;
    }
    if (!criminalRecord.fileName && criminalRecord.documentLink) {
      criminalRecord.fileName = fileNameFromSharedUrl(criminalRecord.documentLink as string);
    }
    criminalRecord.markValidated =
      criminalRecord.markValidated === true ||
      criminalRecord.status === 'validated' ||
      criminalRecord.validationStatus === 'validated';
    merged.criminalRecord = criminalRecord;
  }
  if (myDocuments.documentLink) {
    merged.certDocumentLink = myDocuments.documentLink;
    merged.certDocumentName = myDocuments.documentName;
    merged.attestationNumber = myDocuments.attestationNumber;
    merged.obtentionDate = myDocuments.obtentionDate;
  }
}

/** Copy contract document fields onto the form bag. */
function applyContractToForm(
  merged: Record<string, any>,
  contract: Record<string, any> | undefined,
) {
  if (!contract) return;
  merged.isValid = contract.isValid;
  merged.contractDocumentLink = contract.documentLink;
  merged.contractDocumentName = contract.documentName;
  merged.signedDate = contract.signedDate;
}

/** Copy engagement dates, programs, and weekly availability. */
function applyEngagementToForm(
  merged: Record<string, any>,
  myEngagement: Record<string, any> | undefined,
) {
  if (!myEngagement) return;
  merged.tutoringType = 'in_person';
  merged.coursePath = myEngagement.coursePath === 'classic' ? 'standard' : myEngagement.coursePath;
  if (myEngagement.programs !== undefined) {
    // Normalise program IDs (may be objects or strings)
    merged.programs = (myEngagement.programs as any[]).map((p: any) =>
      typeof p === 'string' ? p : (p?.programId ?? p?.id ?? p),
    );
  }
  if (myEngagement.availability !== undefined) merged.availability = myEngagement.availability;
  // Subjects: always resolve as array. Primary source is the array field; fall back to
  // wrapping the single subjectToTeach value so edit mode always loads all subjects.
  if (Array.isArray(myEngagement.subjects) && myEngagement.subjects.length > 0) {
    // Normalize: extract UUID from objects; keep strings as-is (legacy names resolved later via useEffect)
    merged.subjects = (myEngagement.subjects as any[]).map((s: any) =>
      typeof s === 'string' ? s : (s?.id ?? s?.subjectId ?? s),
    );
  } else if (myEngagement.subjectToTeach) {
    const st = myEngagement.subjectToTeach;
    merged.subjects = [typeof st === 'string' ? st : (st?.id ?? st?.subjectId ?? st)];
  }
}

/** Coerce availability to `{ weekly }` when the API stored a raw array. */
function normalizeAvailability(merged: Record<string, any>) {
  if (!Array.isArray(merged.availability)) return;
  merged.availability = {
    weekly: (merged.availability as any[]).map((slot: any) => ({
      day: slot.dayOfTheWeek ?? slot.day,
      start: slot.startTime ?? slot.start,
      end: slot.endTime ?? slot.end,
    })),
  };
}

/** Copy user identity (name, email, phone, gender) from the record. */
function applyUserIdentityToForm(merged: Record<string, any>, record: any, defaultCountry: string) {
  const user = record.user;
  merged.firstName = user?.firstName ?? '';
  merged.lastName = user?.lastName ?? '';
  merged.canAddStudentsToSession = formBool(user?.userProfiles?.[0]?.canAddStudentsToSession);
  merged.canRemoveStudentsFromSession = formBool(
    user?.userProfiles?.[0]?.canRemoveStudentsFromSession,
  );

  const hydrated = hydrateUserContactFields(
    user?.email,
    user?.phone,
    defaultCountry,
    user?.primaryContactKind,
  );
  merged.contact = hydrated.contact;
  merged.contactType = hydrated.contactType;
  merged.contactCountryCode = hydrated.contactCountryCode;
  merged.secondaryContact = hydrated.secondaryContact;
  merged.secondaryContactCountryCode = hydrated.secondaryContactCountryCode;
  const profileBirthDate = user?.userProfiles?.[0]?.dateOfBirth;
  if (!merged.dateOfBirth && typeof profileBirthDate === 'string' && profileBirthDate) {
    merged.dateOfBirth = profileBirthDate.slice(0, 10);
  }
}

/** Flatten `{ departmentId }` residence department down to a string id. */
function applyDepartmentObject(merged: Record<string, any>) {
  if (merged.department && typeof merged.department === 'object') {
    merged.department = (merged.department as any).departmentId ?? merged.department;
  }
}

/** True when the stored criminal record is already validated. */
export function isCriminalRecordAlreadyValidated(record: Record<string, any> | undefined): boolean {
  if (!record) return false;
  return (
    record.markValidated === true ||
    record.status === 'validated' ||
    record.validationStatus === 'validated'
  );
}

/** Copy tutor/folder situation flags from full-state onto the form. */
function applyStateFlags(merged: Record<string, any>, fullState: unknown) {
  const state = fullState as Record<string, any>;
  merged.preValidated = formBool(state.preValidated);
  merged.validated = formBool(state.validated);
  if (state.criminalRecord && typeof state.criminalRecord === 'object') {
    merged.criminalRecord = {
      ...merged.criminalRecord,
      ...state.criminalRecord,
      markValidated: isCriminalRecordAlreadyValidated(state.criminalRecord),
    };
  }
}

/** Split nested professional-situation payload into form fields. */
function applyProfessionalSituationToForm(merged: Record<string, any>) {
  if (!merged.professionalSituation || typeof merged.professionalSituation !== 'object') return;
  const flat = flattenProfessionalSituation(merged.professionalSituation as Record<string, any>);
  const status = flat?.status ?? '';
  merged.professionalSituation = status;
  merged.proSituationAssociativeEngagement = flat?.associativeEngagement || '';
  merged.proSituationEstablishmentId = flat?.establishment ?? '';
  merged.proSituationEstablishmentName = flat?.establishmentName ?? '';
}

/** Copy profile subjects and target/done hours. */
function applyProfileSubjectsAndHours(merged: Record<string, any>, profile: any) {
  if (Array.isArray(profile.subjects) && profile.subjects.length > 0) {
    merged.subjects = profile.subjects.map((s: any) =>
      typeof s === 'string' ? s : (s?.id ?? s?.subjectId ?? s),
    );
  }
  if (profile.targetHours != null && !merged.targetHours) {
    merged.targetHours = profile.targetHours;
  }
}

/** Copy deactivation/exit fields from the user profile. */
function applyProfileExitFields(merged: Record<string, any>, profile: any) {
  merged.exitReason = normalizeInPersonExitReason(profile.exitReason ?? '');
  merged.exitReasonNote = profile.exitReasonNote ?? '';
  merged.exitDate = profile.exitDate ? String(profile.exitDate).slice(0, 10) : '';
}

/** Apply remaining flattened profile fields (birth, address, hours, exit). */
function applyProfileFields(merged: Record<string, any>, profile: any) {
  if (profile) {
    applyProfileSubjectsAndHours(merged, profile);
  }

  merged.engagementStartDate =
    (merged.desiredStartDate as string | undefined) ??
    (profile?.engagementStartDate as string | null | undefined) ??
    null;
  if (!merged.engagementEndDate && profile?.engagementEndDate) {
    merged.engagementEndDate = profile.engagementEndDate;
  }

  if (profile) {
    applyProfileExitFields(merged, profile);
  }
}

/** Hydrate the edit form from GET full-state. */
export function buildEditInitialData(
  fullState: unknown,
  defaultCountry: string,
  programs?: ReadonlyArray<Pick<Program, 'id' | 'coursePathType'>>,
): Record<string, any> {
  const record = (fullState as any).record;
  const sections = (record?.sections ?? {}) as Record<string, unknown>;
  const merged = mergeSectionValues(sections);

  applyUserIdentityToForm(merged, record, defaultCountry);
  if (typeof merged.dateOfBirth === 'string') {
    merged.dateOfBirth = merged.dateOfBirth.slice(0, 10);
  }
  applyDepartmentObject(merged);
  applyEstablishmentsToForm(merged, sections.my_establishments as Record<string, any> | undefined);
  applyInfoSessionToForm(merged, sections.information_session as Record<string, any> | undefined);
  applyDocumentsToForm(merged, sections.my_documents as Record<string, any> | undefined);
  applyContractToForm(merged, sections.contract as Record<string, any> | undefined);
  applyEngagementToForm(merged, sections.my_engagement as Record<string, any> | undefined);
  normalizeAvailability(merged);

  merged.tutorSituation = record?.tutorSituation ?? null;
  applyStateFlags(merged, fullState);
  applyProfessionalSituationToForm(merged);
  applyProfileFields(merged, record.user?.userProfiles?.[0]);
  if (!merged.country) merged.country = getCompanyCountry();
  applyCatalogCoursePath(merged, programs);
  return merged;
}

/** Invite-only fields sent on create (email, phone, first/last name). */
function applyCreateInviteFields(
  dto: InPersonTutorProfileDto,
  formData: Record<string, any>,
  defaultCountry: string,
) {
  const contact = formData.contact ?? '';
  const contactType = formData.contactType ?? (contact.includes('@') ? 'email' : 'phone');
  dto.inviteBy = contactType === 'email' ? 'email' : 'phone';
  dto.primaryContactKind = primaryContactKindFromType(contactType);
  if (contactType === 'email') {
    dto.email = contact;
    if (formData.secondaryContact?.trim()) {
      dto.phone = phoneToE164(
        formData.secondaryContact.trim(),
        formData.secondaryContactCountryCode || defaultCountry,
      );
    }
  } else {
    dto.phone = phoneToE164(contact, formData.contactCountryCode || defaultCountry);
    if (formData.secondaryContact?.trim()) {
      dto.email = formData.secondaryContact.trim();
    }
  }
  dto.firstName = formData.firstName;
  dto.lastName = formData.lastName;
}

/** Nested professional-situation DTO, same serializer as the remote tutor form. */
function buildProfessionalSituationPayload(
  formData: Record<string, any>,
): string | Record<string, unknown> | undefined {
  const status = formData.professionalSituation as string | undefined;
  if (!status) return undefined;

  const serialized = serializeProfessionalSituation(
    {
      status,
      establishment: formData.proSituationEstablishmentId,
      establishmentName: formData.proSituationEstablishmentName,
      associativeEngagement:
        status === 'employee' ? undefined : formData.proSituationAssociativeEngagement,
    },
    'tutor',
  );
  const bucket = serialized?.[status];
  if (!bucket || typeof bucket !== 'object' || Object.keys(bucket).length === 0) return status;
  return serialized;
}

/** Copy address / zip / city / country onto the profile DTO. */
function applyAddressFields(dto: InPersonTutorProfileDto, formData: Record<string, any>) {
  if (formData.address) dto.address = formData.address;
  if (formData.zipCode) dto.zipCode = formData.zipCode;
  if (formData.city) dto.city = formData.city;
  if (formData.country) dto.country = formData.country;
  if (formData.department) {
    dto.department =
      typeof formData.department === 'object'
        ? (formData.department as any).departmentId
        : (formData.department as string);
  }
}

/** Optional scalars that are omitted when empty (hours, gender, birth date). */
function applyMiscOptionalScalars(dto: InPersonTutorProfileDto, formData: Record<string, any>) {
  const scalarKeys = [
    'gender',
    'dateOfBirth',
    'engagementStartDate',
    'engagementEndDate',
    'participatedAt',
    'criminalRecordDocumentLink',
  ] as const;

  for (const key of scalarKeys) {
    const value = formData[key];
    if (!value) continue;
    if (key === 'criminalRecordDocumentLink' && !storedDocumentLink(value)) continue;
    (dto as Record<string, unknown>)[key] = value;
  }
  if (formData.sessionId !== undefined) {
    dto.sessionId = formData.sessionId || null;
  }

  dto.hasParticipated = formBool(formData.hasParticipated);
  if (formData.markedBy !== undefined) {
    (dto as Record<string, unknown>).markedBy = formData.markedBy;
  }
  if (formData.targetHours) dto.targetHours = Number(formData.targetHours);
  if (formData.availability) dto.availability = formData.availability;
}

/** Remaining optional profile scalars (bio, notes, tutoring type). */
function applyOptionalScalars(dto: InPersonTutorProfileDto, formData: Record<string, any>) {
  applyAddressFields(dto, formData);
  dto.tutoringType = 'in_person';
  dto.coursePath = formData.coursePath;

  const proSitPayload = buildProfessionalSituationPayload(formData);
  if (proSitPayload !== undefined) {
    (dto as Record<string, unknown>).professionalSituation = proSitPayload;
  }

  applyMiscOptionalScalars(dto, formData);
}

/** Array fields: programs, subjects, departments, selected establishments. */
function applyListFields(dto: InPersonTutorProfileDto, formData: Record<string, any>) {
  if (formData.departments?.length) dto.departments = formData.departments;
  if (formData.subjects?.length) dto.subjects = formData.subjects;
  if (formData.programs?.length) dto.programs = formData.programs;
  if (formData.selectedEstablishments?.length) {
    dto.wishes = (formData.selectedEstablishments as string[]).map((id, index) => ({
      rank: index + 1,
      establishmentId: id,
    }));
  }
}

/** Contract + criminal-record document links/names onto the DTO. */
function applyDocumentFields(dto: InPersonTutorProfileDto, formData: Record<string, any>) {
  // Criminal record — send full object for metadata fields
  if (formData.criminalRecord && Object.keys(formData.criminalRecord).length > 0) {
    dto.criminalRecord = formData.criminalRecord;
  }
  const certLink = storedDocumentLink(formData.certDocumentLink);
  if (certLink) {
    dto.certDocumentLink = certLink;
    dto.certDocumentName = formData.certDocumentName;
    dto.attestationNumber = formData.attestationNumber;
    dto.obtentionDate = formData.obtentionDate;
  }
  const contractLink = storedDocumentLink(formData.contractDocumentLink);
  if (contractLink) {
    dto.contractDocumentLink = contractLink;
    dto.contractDocumentName = formData.contractDocumentName;
    dto.signedDate = formData.signedDate;
  }
}

/** Write primary + secondary email/phone onto the in-person tutor edit DTO. */
function applyEditContactFields(
  dto: InPersonTutorProfileDto,
  formData: Record<string, any>,
  defaultCountry: string,
): void {
  dto.primaryContactKind = primaryContactKindFromType(formData.contactType);
  if (formData.contactType === 'email' && formData.contact) {
    dto.email = formData.contact.trim();
  } else if (formData.contactType === 'phone' && formData.contact) {
    dto.phone = phoneToE164(formData.contact, formData.contactCountryCode || defaultCountry);
  }
  const secondaryPhoneVal =
    formData.contactType === 'email' ? formData.secondaryContact : undefined;
  if (secondaryPhoneVal) {
    dto.phone = phoneToE164(
      secondaryPhoneVal,
      formData.secondaryContactCountryCode || defaultCountry,
    );
  }
  const secondaryEmailVal =
    formData.contactType === 'phone' ? formData.secondaryContact : undefined;
  if (secondaryEmailVal) dto.email = secondaryEmailVal;
}

/** Build the create/edit profile DTO from form state. */
export function buildTutorProfileDto(
  mode: 'create' | 'edit',
  formData: Record<string, any>,
  defaultCountry: string,
): InPersonTutorProfileDto {
  const dto: InPersonTutorProfileDto = {};
  if (mode === 'create') applyCreateInviteFields(dto, formData, defaultCountry);
  if (mode === 'edit') {
    if (formData.firstName) dto.firstName = formData.firstName;
    if (formData.lastName) dto.lastName = formData.lastName;
    applyEditContactFields(dto, formData, defaultCountry);
  }
  applyOptionalScalars(dto, formData);
  applyListFields(dto, formData);
  applyDocumentFields(dto, formData);
  dto.canAddStudentsToSession = formBool(formData.canAddStudentsToSession);
  dto.canRemoveStudentsFromSession = formBool(formData.canRemoveStudentsFromSession);
  dto.preValidated = formBool(formData.preValidated);
  dto.validated = formBool(formData.validated);
  return dto;
}

/** Error string when engagement end is before start; otherwise undefined. */
export function checkEngagementEndDateError(
  startDate: string | undefined,
  endDate: string | undefined,
): string | undefined {
  if (!startDate || !endDate) return undefined;
  if (endDate < startDate) {
    return getErrorMessage('engagementEndBeforeStart');
  }
  return undefined;
}

/** After create: mark the criminal record validated when the checkbox is on. */
export async function applyCriminalRecordOnCreate(
  userId: string | undefined,
  markValidated: boolean,
  companyId: string,
  mutate: CriminalRecordDecisionFn,
) {
  if (!userId || !markValidated) return;
  try {
    await mutate({ userId, companyId, action: 'validate' });
  } catch (err) {
    console.warn('[TutorForm] Criminal record decision failed at creation:', err);
  }
}

/** After edit: validate or reset the criminal-record decision if the checkbox changed. */
export async function syncCriminalRecordOnEdit(
  tutorUserId: string | undefined,
  nowValidated: boolean,
  wasValidated: boolean,
  companyId: string,
  mutate: CriminalRecordDecisionFn,
) {
  if (!tutorUserId || nowValidated === wasValidated) return;
  try {
    await mutate({
      userId: tutorUserId,
      companyId,
      action: nowValidated ? 'validate' : 'cancel',
    });
  } catch (err) {
    console.warn('[TutorForm] Criminal record decision failed:', err);
  }
}

/** After save: direct-assign or retain establishments when the checkboxes actually changed. */
export async function applyAssignmentAfterSubmit(params: {
  userId: string | undefined;
  selectedEstablishmentIds: string[];
  tutorSituation: string | undefined;
  validated: boolean;
  preValidated: boolean;
  initialValidated: boolean;
  initialPreValidated: boolean;
  canValidateAssignment: boolean;
  canPrevalidate: boolean;
  companyId: string;
  directAssign: (args: {
    userId: string;
    companyId: string;
    establishmentId: string;
  }) => Promise<unknown>;
  retain: (args: {
    userId: string;
    companyId: string;
    establishmentIds: string[];
  }) => Promise<unknown>;
}): Promise<'validated' | 'prevalidated' | undefined> {
  const {
    userId,
    selectedEstablishmentIds,
    tutorSituation,
    validated,
    preValidated,
    initialValidated,
    initialPreValidated,
    canValidateAssignment,
    canPrevalidate,
    companyId,
    directAssign,
    retain,
  } = params;
  if (!userId || selectedEstablishmentIds.length === 0 || tutorSituation === 'deactivated') {
    return undefined;
  }

  const isNewValidation = validated && !initialValidated;
  const isNewPreValidation = preValidated && !initialPreValidated;

  if (isNewValidation && canValidateAssignment) {
    await directAssign({
      userId,
      companyId,
      establishmentId: selectedEstablishmentIds[0],
    });
    return 'validated';
  }
  if (isNewPreValidation && canPrevalidate) {
    await retain({
      userId,
      companyId,
      establishmentIds: selectedEstablishmentIds,
    });
    return 'prevalidated';
  }
  return undefined;
}

/** Map establishments to {@link Autocomplete} `{ value, label }`. */
export function mapEstablishmentOptions(
  establishments: Array<{ id?: string; name?: string }>,
): Array<{ value: string; label: string }> {
  return establishments.map((e) => ({ value: e.id as string, label: e.name as string }));
}

/** Filter options by case-insensitive label query. */
export function filterOptionsBySearchQuery<T extends { label: string }>(
  options: T[],
  query: string,
): T[] {
  if (!query) return options;
  const q = query.toLowerCase();
  return options.filter((o) => o.label.toLowerCase().includes(q));
}

/** Union weekly slots from the selected programs. */
export function computeInPersonAllowedSlotsFromPrograms(
  programs: Array<{
    id: string;
    availability?: { weekly?: Array<{ start: string; end: string }> };
  }>,
  selectedProgramIds: string[] | undefined,
): Array<{ start: string; end: string }> | undefined {
  if (!selectedProgramIds?.length) return undefined;
  const selected = programs.filter((p) => selectedProgramIds.includes(p.id));
  const slots = selected
    .flatMap((p) => p.availability?.weekly ?? [])
    .map((s) => ({ start: s.start, end: s.end }));
  return slots.length > 0 ? slots : undefined;
}

/** Official in-person situations that require a valued-associative-engagement answer. */
const VALUED_ENGAGEMENT_REQUIRED_SITUATIONS = new Set(['student', 'high_school_student', 'other']);

/** True when the in-person tutor form must collect valued associative engagement. */
export function isValuedEngagementRequired(professionalSituation?: string): boolean {
  return (
    !!professionalSituation && VALUED_ENGAGEMENT_REQUIRED_SITUATIONS.has(professionalSituation)
  );
}

/** Error when a required establishment or valued-engagement answer is missing. */
export function validateInPersonProSituationFields(formData: {
  professionalSituation?: string;
  proSituationEstablishmentId?: string;
  proSituationAssociativeEngagement?: string;
}): string | undefined {
  if (
    (formData.professionalSituation === 'student' ||
      formData.professionalSituation === 'high_school_student') &&
    !formData.proSituationEstablishmentId
  ) {
    return getErrorMessage('requiredEstablishmentName');
  }
  if (
    isValuedEngagementRequired(formData.professionalSituation) &&
    !formData.proSituationAssociativeEngagement
  ) {
    return getErrorMessage('valuedEngagementRequired');
  }
  return undefined;
}

/** Map leftover subject labels to catalog ids; no-op when already UUIDs. */
export function normalizeTutorSubjectIds(
  subjects: string[] | undefined,
  subjectOptions: Array<{ value: string; label: string }>,
): string[] | null {
  if (!subjectOptions.length || !subjects?.length) return null;
  const hasNonUUID = subjects.some((s) => !TUTOR_SUBJECT_UUID_REGEX.test(s));
  if (!hasNonUUID) return null;
  return subjects
    .map((s) => {
      if (TUTOR_SUBJECT_UUID_REGEX.test(s)) return s;
      const match = subjectOptions.find((o) => o.label.toLowerCase() === s.toLowerCase());
      return match?.value ?? null;
    })
    .filter(Boolean) as string[];
}

/** Checkbox pair from affectation statuses (validated / awaiting coord). */
export function deriveAssignmentCheckboxStateFromAffectations(
  affectations: Array<{ status?: string }>,
): { validated: boolean; preValidated: boolean } | null {
  if (!affectations.length) return null;
  const hasValidated = affectations.some((a) => a.status === 'validated');
  const hasAwaitingCoord = affectations.some((a) => a.status === 'awaiting_coordinator_validation');
  return {
    validated: hasValidated,
    preValidated: hasValidated || hasAwaitingCoord,
  };
}

/** Patch one tutor-form field; clear establishments when departments change. */
export function applyInPersonTutorFieldUpdate(
  prev: Record<string, any>,
  field: string,
  value: unknown,
): Record<string, any> {
  const next = { ...prev, [field]: value };
  if (field === 'departments') next.selectedEstablishments = [];
  return next;
}

/** Form patch for a contract upload (or clear when `url` is empty). */
export function buildContractPatch(url: string): Record<string, string> {
  if (url) return { contractDocumentLink: url, contractDocumentName: fileNameFromSharedUrl(url) };
  return { contractDocumentLink: '', contractDocumentName: '' };
}

/** Prepend a selected id/name as the first {@link Autocomplete} option. */
export function prependAutocompleteSeedOption(
  options: Array<{ value: string; label: string }>,
  id: string,
  name: string,
): Array<{ value: string; label: string }> {
  if (!name) return options;
  const seed = { value: id || '__manual__', label: name };
  if (options.some((o) => o.value === seed.value)) return options;
  return [seed, ...options];
}

/** Resolve {@link Autocomplete} value to `{ id, label }`. */
export function mapAutocompleteIdLabelSelection(
  value: string,
  options: Array<{ value: string; label: string }>,
): { id: string; label: string } {
  const selected = options.find((o) => o.value === value);
  return { id: value, label: selected?.label ?? '' };
}

/** Hide "already participated" fields while an information-meeting slot is selected. */
export function shouldShowInformationMeetingParticipationFields(formData: {
  sessionId?: unknown;
  hasParticipated?: unknown;
}): boolean {
  return !formData.sessionId || formData.hasParticipated === true;
}

/** Patch information-meeting participation (stamp date + marker on check). */
export function buildInformationMeetingParticipationPatch(
  checked: boolean,
  current: { participatedAt?: string | null; markedBy?: unknown },
  user?: { id: string; firstName?: string; lastName?: string } | null,
): Record<string, unknown> {
  if (!checked) {
    return { hasParticipated: false, participatedAt: null, markedBy: null };
  }
  const patch: Record<string, unknown> = { hasParticipated: true };
  if (!current.participatedAt) {
    patch.participatedAt = new Date().toISOString();
  }
  if (user) {
    patch.markedBy = {
      userId: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
    };
  }
  return patch;
}

/** Latest birth date (YYYY-MM-DD) for a tutor who is already the minimum age. */
export function getTutorMaxBirthDate(from = new Date()): string {
  const date = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  date.setFullYear(date.getFullYear() - TUTOR_MIN_AGE);
  return toLocalDateString(date);
}

/** Error when a filled birth date is younger than the tutor minimum age. */
export function getTutorMinAgeError(dateOfBirth: string | undefined): string | undefined {
  const calendar = toCalendarDateString(dateOfBirth);
  if (!calendar) return undefined;
  if (calendar > getTutorMaxBirthDate()) {
    return getErrorMessage({ code: 'tutorMinAge', minAge: TUTOR_MIN_AGE });
  }
  return undefined;
}

/** Patch contract / cert links; criminal-record only when `includeCriminalRecord` (create, not edit). */
export function mergePendingUploads<T extends DocumentLinkFields>(
  formData: T,
  uploadedUrls: Record<string, string>,
  includeCriminalRecord = true,
): T {
  return {
    ...formData,
    ...(uploadedUrls.contractFile ? { contractDocumentLink: uploadedUrls.contractFile } : {}),
    ...(uploadedUrls.certFile ? { certDocumentLink: uploadedUrls.certFile } : {}),
    ...(includeCriminalRecord && uploadedUrls.criminalRecordFile
      ? { criminalRecordDocumentLink: uploadedUrls.criminalRecordFile }
      : {}),
  };
}
